import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLElement} [loadMore] - The load more pagination container.
 * @property {HTMLElement} [loadMoreText] - The load more progress text.
 * @property {HTMLElement} [loadMoreTrack] - The load more progress track.
 * @property {HTMLElement} [loadMoreProgress] - The load more progress bar fill.
 * @property {HTMLAnchorElement} [loadMoreButton] - The load more link (href = next page).
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #isLoadingMore = false;

  /**
   * When a price-range filter is active, VAT Liquid post-filter can wipe a Shopify
   * page to zero cards. Once a load-more fetch appends nothing, treat pagination
   * as exhausted (finalize UI from DOM card count). Unused without a price filter.
   *
   * @type {boolean}
   */
  #isExhausted = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    const { loadMore, loadMoreButton, cards } = this.refs;
    const currentPage =
      loadMore && Array.isArray(cards) && cards[0] ? Number(cards[0].dataset.page) || 1 : 1;
    if (currentPage > 1 && loadMoreButton instanceof HTMLAnchorElement) {
      this.#setLoadMoreBusy(true);
    }

    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);

    void this.#initPagination();
  }

  /**
   * Prefetch neighbors; for load-more deep links, hydrate earlier pages first.
   */
  async #initPagination() {
    if (this.refs.loadMore) {
      await this.#hydrateLoadMoreDeepLink();
    }

    this.#fetchPage('next');
    if (this.refs.viewMorePrevious) {
      this.#fetchPage('previous');
    }
  }

  /**
   * When landing on ?page=N with button pagination, only page N is in the DOM.
   * Prefetch and prepend pages 1..N-1 so card count matches the progress label.
   */
  async #hydrateLoadMoreDeepLink() {
    const { loadMore, loadMoreButton, grid, cards } = this.refs;
    if (!loadMore || !grid || !Array.isArray(cards) || cards.length === 0) return;

    const currentPage = Number(cards[0].dataset.page) || 1;
    if (currentPage <= 1) {
      this.#updateLoadMoreUI();
      return;
    }

    this.#isLoadingMore = true;
    this.#setLoadMoreBusy(true);
    loadMore.setAttribute('aria-busy', 'true');

    const anchorCard = cards[0];

    try {
      for (let page = currentPage - 1; page >= 1; page--) {
        await this.#fetchSpecificPage(page);
        const pageItems = this.#getGridForPage(page);
        if (pageItems?.length) {
          grid.prepend(...pageItems);
        }
      }

      this.#aspectRatioHelper?.processNewElements();
      anchorCard.scrollIntoView({ block: 'start', behavior: 'instant' });
    } finally {
      this.#isLoadingMore = false;
      loadMore.removeAttribute('aria-busy');
      this.#updateLoadMoreUI();

      if (
        loadMoreButton instanceof HTMLAnchorElement &&
        !loadMoreButton.hidden &&
        !this.#isExhausted
      ) {
        this.#setLoadMoreBusy(false);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Loads the next page of results (button pagination mode).
   * Plain clicks are intercepted; modified clicks follow the crawlable href.
   *
   * @param {Event} [event]
   */
  async loadMore(event) {
    if (event instanceof MouseEvent) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
        return;
      }
      event.preventDefault();
    } else if (event instanceof Event) {
      event.preventDefault();
    }

    if (this.#isLoadingMore || this.#isLoadMoreBusy()) return;

    const { loadMoreButton } = this.refs;
    this.#isLoadingMore = true;
    this.#setLoadMoreBusy(true);

    try {
      const appended = await this.#renderNextPage();

      if (appended === 0 && this.#isPriceRangeFilterActive()) {
        this.#isExhausted = true;
      }

      this.#updateLoadMoreUI();
    } finally {
      this.#isLoadingMore = false;

      if (
        loadMoreButton instanceof HTMLAnchorElement &&
        !loadMoreButton.hidden &&
        !this.#isExhausted
      ) {
        this.#setLoadMoreBusy(false);
      }
    }
  }

  /**
   * @returns {boolean}
   */
  #isLoadMoreBusy() {
    const { loadMoreButton } = this.refs;
    return loadMoreButton?.getAttribute('aria-disabled') === 'true';
  }

  /**
   * @param {boolean} busy
   */
  #setLoadMoreBusy(busy) {
    const { loadMoreButton } = this.refs;
    if (!(loadMoreButton instanceof HTMLAnchorElement)) return;

    if (busy) {
      loadMoreButton.setAttribute('aria-disabled', 'true');
      loadMoreButton.setAttribute('tabindex', '-1');
      loadMoreButton.setAttribute('aria-busy', 'true');
    } else {
      loadMoreButton.removeAttribute('aria-disabled');
      loadMoreButton.removeAttribute('tabindex');
      loadMoreButton.removeAttribute('aria-busy');
    }
  }

  /**
   * Keep the load-more href pointed at the next page for crawlers / new-tab.
   */
  #syncLoadMoreHref() {
    const { loadMoreButton } = this.refs;
    if (!(loadMoreButton instanceof HTMLAnchorElement)) return;

    const nextPage = this.#getPage('next');
    if (nextPage?.url && this.#shouldUsePage(nextPage)) {
      loadMoreButton.href = nextPage.url.toString();
    }
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @returns {boolean} Whether a next page is available
   */
  #hasNextPage() {
    return this.#shouldUsePage(this.#getPage('next'));
  }

  /**
   * True when a Shopify price-range filter is in the URL (gte and/or lte).
   * Gates empty-page exhaustion: VAT post-filter only applies with this filter.
   *
   * @returns {boolean}
   */
  #isPriceRangeFilterActive() {
    const params = new URL(window.location.href).searchParams;

    return params.has('filter.v.price.gte') || params.has('filter.v.price.lte');
  }

  /**
   * Updates load-more progress text, bar, and visibility after appending cards.
   */
  #updateLoadMoreUI() {
    const { loadMore, loadMoreText, loadMoreTrack, loadMoreProgress, loadMoreButton, cards } =
      this.refs;
    if (!loadMore) return;

    const shown = Array.isArray(cards) ? cards.length : 0;
    const template = loadMore.dataset.textTemplate || '';

    // With a price filter, a VAT-wiped page finalizes the total from DOM cards;
    // otherwise the denominator stays Shopify's catalog total.
    const total = this.#isExhausted ? shown : Number(loadMore.dataset.totalItems) || 0;
    const isComplete = this.#isExhausted || shown >= total || !this.#hasNextPage();
    const percent = total > 0 ? Math.min(100, (shown / total) * 100) : 100;
    const text = template.replaceAll('[[shown]]', String(shown)).replaceAll('[[total]]', String(total));

    if (this.#isExhausted) {
      loadMore.dataset.totalItems = String(total);
    }

    if (loadMoreText) {
      loadMoreText.textContent = text;
    }

    if (loadMoreTrack instanceof HTMLElement) {
      loadMoreTrack.setAttribute('aria-valuenow', String(shown));
      loadMoreTrack.setAttribute('aria-valuemax', String(total));
      loadMoreTrack.setAttribute('aria-label', text);
    }

    if (loadMoreProgress instanceof HTMLElement) {
      loadMoreProgress.style.width = `${percent}%`;
    }

    if (!isComplete) {
      this.#syncLoadMoreHref();
      return;
    }

    // `.button[hidden]` in base.css beats the button's own display rule; the
    // container's `display: flex` would win over the UA `[hidden]` rule.
    if (loadMoreButton instanceof HTMLAnchorElement) {
      loadMoreButton.hidden = true;
    }

    if (!this.#isExhausted) {
      loadMore.hidden = true;
    }
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * Appends the next page of grid items.
   *
   * @returns {Promise<number>} Number of product-grid items appended
   */
  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return 0;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return 0;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return 0;
    }

    const appended = nextPageItemElements.length;

    // With a price filter, VAT post-filter can return an empty page. Do not
    // advance the URL or prefetch further — loadMore will finalize UI instead.
    if (appended === 0 && this.#isPriceRangeFilterActive()) {
      return 0;
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', nextPage.url.toString());

    requestIdleCallback(() => {
      this.#fetchPage('next');
    });

    return appended;
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    // Include packshots (.product-grid__item--packshot) which omit ref="cards[]"
    // so product counts stay product-only via refs.cards.
    return gridElement.querySelectorAll(':scope > .product-grid__item');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();
    this.#isExhausted = false;

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
